import React from 'react';
import { DataSet, LabelPosition } from './types';
import { feedback } from '@ecosystem/newron-design-system';
import { useMobile } from '../../context/mobile/mobile.provider';
import classNames from 'classnames';

type Props = {
  data: DataSet[];
  totalsLabel?: string;
  totalsChangeLabel?: string;
  additionalTotalsLabel?: string;
  additionalTotalsChangeLabel?: string;
  css?: string;
  additionalLabelPosition?: LabelPosition;
};
function Legend({
  css,
  data,
  totalsLabel,
  totalsChangeLabel,
  additionalTotalsLabel,
  additionalTotalsChangeLabel,
  additionalLabelPosition = LabelPosition.BOTTOM,
}: Props) {
  const uniqueIds = new Set<string>();
  const items: { uniqueId: string; label: string; subLabel?: string; color: string }[] = [];
  const CHARACTER_LIMIT = 16;
  const LEGEND_HEIGHT = '483px';

  const { Tooltip } = feedback;
  const { isMobile } = useMobile();
  data?.forEach(set => {
    const idValue = set.uniqueId || set.label;
    if (!uniqueIds.has(idValue)) {
      uniqueIds.add(idValue);
      items.push({
        uniqueId: idValue,
        label: set.label,
        subLabel: set.subLabel,
        color: set.values.fillColor[0],
      });
    }
  });

  const legendCss = `flex ${additionalTotalsChangeLabel ? 'pb-8' : ''} ${
    additionalLabelPosition === LabelPosition.BOTTOM ? 'flex-column' : 'flex-column-reverse'
  } ${isMobile ? '' : css || ''}`;

  return (
    <div data-test-id={'chart-legend-label-container'} className={legendCss}>
      {totalsLabel && totalsChangeLabel && !isMobile && (
        <div className="text-sm leading-tight">
          <div className="text-default">{totalsLabel}</div>
          <div className="text-lighter">{totalsChangeLabel}</div>
        </div>
      )}
      <div
        data-test-id={'chart-legends-container'}
        className={classNames('flex  flex-grow ', {
          'flex-row flex-wrap items-center  gap-1': isMobile,
          'flex-column justify-center min-w-40 max-w-44': !isMobile,
        })}
        style={{
          marginTop: !isMobile ? '30px' : '',
          marginBottom: !isMobile && additionalTotalsLabel && additionalTotalsChangeLabel ? '56px' : '0px',
          minHeight: isMobile ? '50px' : LEGEND_HEIGHT,
          maxHeight: LEGEND_HEIGHT,
        }}
      >
        {items.map(item => {
          const isLongLabel = item.label.length > CHARACTER_LIMIT;
          const isDualLine = item.subLabel && item.subLabel.length > 0;

          return (
            <div key={item.uniqueId}>
              <Tooltip placement={'top'}>
                {isLongLabel && (
                  <Tooltip.Content>
                    <span className={'break-all'}>{item.label}</span>
                  </Tooltip.Content>
                )}
                <div className="flex items-start gap-2">
                  <span className="w-4 min-w-4 h-3 inline-block mt-1" style={{ backgroundColor: item.color }} />
                  <span
                    data-test-id={'chart-legends-label'}
                    className={`text-sm truncate text-interactive ${isDualLine ? 'leading-tight' : ''}`}
                    style={{ paddingTop: isDualLine ? '0.5px' : '0px' }}
                  >
                    {item.label}
                  </span>
                </div>
                {item?.subLabel && (
                  <div className="flex pl-6">
                    <span
                      data-test-id={'chart-legends-sub-label'}
                      className={`text-sm truncate text-interactive leading-tight`}
                      style={{ paddingBottom: '0.5px' }}
                    >
                      {item.subLabel}
                    </span>
                  </div>
                )}
              </Tooltip>
            </div>
          );
        })}
      </div>
      {!isMobile && additionalTotalsLabel && additionalTotalsChangeLabel && (
        <div className="text-sm leading-tight">
          <div className="text-default">{additionalTotalsLabel}</div>
          <div className="text-lighter">{additionalTotalsChangeLabel}</div>
        </div>
      )}
    </div>
  );
}

export { Legend };
